<template>
  <span>
    <a
      class="button is-outlined text-sm lg:text-xs"
      title="Edit"
      @click="showingModal = true"
    >
      <i class="far fa-edit"></i>
    </a>

    <modal
      :title="title"
      width="800px"
      @close="showingModal = false"
      v-if="showingModal"
    >
      <h2 class="title is-3">{{ suiteDetails.company.name }}</h2>
      <div class="columns" v-show="suiteDetails.company.name">
        <div class="column is-3">
          <div class="field">
            <label class="image is-square show-icon-hover">
              <span class="icon icon-right is-medium"
                ><i class="fas fa-pen-square fa-2x"></i
              ></span>
              <img
                :src="fixHttpProtocol(suiteDetails.company.logo_path)"
                class="company-logo"
                v-if="suiteDetails.company"
              />
              <input
                type="file"
                ref="newCompanyLogo"
                @change="
                  uploadLogo(
                    $refs.newCompanyLogo.files[0],
                    suiteDetails.company
                  )
                "
                style="display: none"
              />
            </label>
          </div>
        </div>
        <div class="column">
          <div class="field">
            <div class="label">Description</div>
            <textarea
              class="textarea"
              v-model="suiteDetails.company.description"
            ></textarea>
          </div>
        </div>
      </div>
      <div>
        <h5 class="title is-5" v-text="`${suiteOrTeam} Details`"></h5>
        <edit-suite
          :isAdmin="true"
          :suite="suiteDetails"
          :companyContacts="companyContacts"
          :companyId="suiteDetails.company.maxxId"
          @syncContacts="fetchCompanyStaff(suiteDetails.company.maxxId)"
        >
        </edit-suite>
      </div>
      <b-notification
        v-if="noMaxxUser"
        type="is-danger"
        has-icon
        :closable="false"
        class="is-small"
        role="alert"
      >
        Could not retrieve contacts from MatrixMaxx. Your session may have
        expired. Please login to fix this.
      </b-notification>
      <div slot="footer" class="buttons is-right w-full">
        <button
          class="button is-success text-sm lg:text-base"
          :class="{ 'is-loading': loading }"
          @click="saveSuite"
        >
          Save
        </button>
        <button
          class="button is-success text-sm lg:text-base"
          :class="{ 'is-loading': loading }"
          @click="saveSuiteAddAnother"
        >
          Save / Add Another
        </button>
        <button
          class="button text-sm lg:text-base"
          @click="showingModal = false"
        >
          Cancel
        </button>
      </div>
    </modal>
  </span>
</template>

<script>
import Multiselect from 'vue-multiselect';
import Modal from './Modal';
import EditSuite from './EditSuite';
import MaxxAPI from '@/services/maxxapi';
import { db, storage } from '@/services/firebase';
import swal from 'sweetalert';
import { convertToKebabCase, fixHttpProtocol } from '@/services/utils';

export default {
  data() {
    return {
      showingModal: false,
      loading: false,
      companyContacts: [], // list of contacts for the selected company
      suiteDetails: {},
      noMaxxUser: false,
      meeting: null,
      users: []
    };
  },
  firestore() {
    const suite = db
      .collection('meetings')
      .doc(this.meetingId)
      .collection('suites')
      .doc(this.suiteId);
    const users = db
      .collection('meetings')
      .doc(this.meetingId)
      .collection('users');
    const meeting = db.collection('meetings').doc(this.meetingId);

    return {
      suiteDetails: suite,
      users,
      meeting
    };
  },
  props: {
    meetingId: {
      type: String,
      required: true
    },
    suiteId: {
      type: String,
      required: true
    }
  },
  computed: {
    title() {
      if (!this.suiteDetails) return '';
      return `Edit ${this.suiteDetails.type}`;
    },
    suiteOrTeam() {
      if (this.suiteDetails.type === 'Retailer') return 'Team';
      return 'Suite';
    }
  },
  methods: {
    fixHttpProtocol,
    close() {
      this.showingModal = false;
    },
    open() {
      this.showingModal = true;
    },
    fetchCompanyStaff(companyId) {
      MaxxAPI.getCompanyStaff(companyId).then(response => {
        if (response.error && response.error === 'No user') {
          this.loading = false;
          this.noMaxxUser = true;
        } else {
          this.noMaxxUser = false;
          this.companyContacts = response
            .map(item => {
              return {
                id: item.id,
                name: item.Name,
                email: item.Email,
                username: item.username
              };
            })
            .filter(item => {
              return item.username !== '';
            });
        }
      });
    },
    saveSuiteAddAnother() {
      if (this.saveSuite()) {
        this.$emit('addSuite');
      }
    },
    saveSuite() {
      const suiteToSave = {
        name: this.suiteDetails.name,
        description: this.suiteDetails.description,
        logo_path: this.suiteDetails.logo_path,
        contacts: this.suiteDetails.contacts,
        activityWritten: true,
        company: {
          maxxId: this.suiteDetails.company.maxxId,
          name: this.suiteDetails.company.name,
          logo_path: this.suiteDetails.company.logo_path,
          description: this.suiteDetails.company.description
        }
      };

      // validate whether the suite has a primary contact
      if (!this.suiteHasPrimaryContact()) {
        swal({
          text: `The ${this.suiteOrTeam} must have a primary contact assigned before the ${this.suiteDetails.type} can be saved.`,
          icon: 'warning',
          buttons: {
            cancel: {
              text: 'OK',
              value: null,
              visible: true,
              className: 'button is-light',
              closeModal: true
            }
          }
        });
        return false;
      }

      if (this.suiteDetails.activityWritten) {
        this.writeAndCloseModal(suiteToSave);
        return true;
      } else {
        MaxxAPI.createActivity(
          this.meeting.maxxId,
          this.suiteDetails.company.maxxId,
          this.suiteDetails.name,
          'added'
        )
          .then(() => {
            this.writeAndCloseModal(suiteToSave);
            return true;
          })
          .catch(e => {
            swal({
              title: 'No MatrixMaxx session',
              text:
                'Your MatrixMaxx session is no longer active. A session is required to write activity data to MatrixMaxx. Please login and retry this action.',
              icon: 'error'
            });
            return false;
          });
      }
    },
    writeAndCloseModal(suiteToSave) {
      this.$firestoreRefs.suiteDetails.update(suiteToSave);
      this.updateUsers();
      this.showingModal = false;
    },
    updateUsers() {
      // grab all users that are associated with the suite
      const currentSuiteUsers1 = this.$firestoreRefs.users
        .where(`suites.${this.suiteDetails.id}`, '==', true)
        .get();
      const currentSuiteUsers2 = this.$firestoreRefs.users
        .where(`suites.${this.suiteDetails.id}`, '==', false)
        .get();

      Promise.all([currentSuiteUsers1, currentSuiteUsers2]).then(users => {
        const currentUsers = [...users[0].docs, ...users[1].docs].map(
          userDoc => {
            return {
              id: userDoc.id,
              data: userDoc.data()
            };
          }
        );

        // compare against the currently selected contacts
        const removedUsers = currentUsers.filter(user => {
          // if "user" does not exist in suiteDetails.contacts, return true
          const existsInContacts = this.suiteDetails.contacts.some(contact => {
            return user.id === contact.username;
          });

          return !existsInContacts;
        });

        // if a contact exists in the db, but not in the multiselect, remove their entry from db
        removedUsers.forEach(removedUser => {
          // delete the suite from the users collection
          delete removedUser.data.suites[this.suiteDetails.id];
          this.$firestoreRefs.users.doc(removedUser.id).set(removedUser.data);
        });

        // if a contact exists in both, update the boolean value for is_primary
        this.suiteDetails.contacts.forEach(contact => {
          // grab the db record
          let dbUser = currentUsers.filter(user => {
            return user.id === contact.username;
          });

          let userObj = dbUser.length
            ? dbUser[0].data
            : {
                name: contact.name,
                email: contact.email,
                company_id: this.suiteDetails.company.maxxId,
                suites: {},
                type: this.suiteDetails.type
              };

          userObj.suites[this.suiteDetails.id] = contact.is_primary || false;

          this.$firestoreRefs.users
            .doc(contact.username)
            .set(userObj, { merge: true });
        });
      });
    },
    suiteHasPrimaryContact() {
      if (this.suiteDetails.contacts && this.suiteDetails.contacts.length) {
        return this.suiteDetails.contacts.some(con => {
          return con.is_primary;
        });
      }
      return false;
    },
    confirmDeleteSuite(suite) {
      swal({
        title: 'Are you sure?',
        text: `By proceeding, ${suite.name} and all of its time slots will be removed.`,
        icon: 'warning',
        buttons: {
          cancel: {
            text: 'Cancel',
            value: null,
            visible: true,
            className: 'button is-light',
            closeModal: true
          },
          confirm: {
            text: 'Yes, remove suite',
            value: true,
            visible: true,
            className: 'button is-danger is-inverted',
            closeModal: true
          }
        },
        dangerMode: true
      }).then(proceed => {
        if (proceed) {
          this.deleteSuite(suite.id);
          this.updateUsers();
        }
      });
    },
    removeViewOnly(contact) {
      this.viewOnlyContacts = this.viewOnlyContacts.filter(
        selectedContact => contact.id !== selectedContact.id
      );
    },
    uploadLogo(file, company) {
      const ref = storage.ref();
      const name =
        'logos/' +
        convertToKebabCase(company.name) +
        '-' +
        +new Date() +
        '-' +
        file.name;
      const metadata = { contentType: file.type };
      const task = ref.child(name).put(file, metadata);

      // display the new logo immediately
      const reader = new FileReader();
      reader.addEventListener(
        'load',
        function() {
          company.logo_path = reader.result;
        },
        false
      );
      reader.readAsDataURL(file);

      task.then(snapshot => {
        snapshot.ref.getDownloadURL().then(downloadURL => {
          company.logo_path = downloadURL;
        });
      });
    }
  },
  components: {
    Multiselect,
    Modal,
    EditSuite
  },
  watch: {
    async showingModal(newValue, oldValue) {
      if (
        this.suiteDetails &&
        newValue === true &&
        this.companyContacts.length === 0
      ) {
        this.fetchCompanyStaff(this.suiteDetails.company.maxxId);
      }
      console.log(newValue);
      if (newValue === true) {
        const suite = await db
          .collection('meetings')
          .doc(this.meetingId)
          .collection('suites')
          .doc(this.suiteId);
        this.$bind('suiteDetails', suite);
      }
    }
  }
};
</script>

<style scoped>
.show-icon-hover {
  position: relative;
  cursor: pointer;
}

span.icon-right {
  opacity: 0;
  position: absolute;
  top: 0;
  right: 0;
  transition: opacity 0.2s linear;
}

.show-icon-hover:hover span.icon-right {
  opacity: 1;
}
</style>



// WEBPACK FOOTER //
// src/components/shared/ModalEditSuite.vue